import axiosInstance from '@/api/axiosInstance';
import { buildQueryString } from '@/utils/query';
import { ExportFormat } from '@/constants/export-dropdown.constants';
import { RoleCreate, Role, RoleFilterParams, PaginatedRolesResponse, RoleUpdate } from '@/types/role.types';
import { PaginatedPermissionsResponse, PermissionFilterParams } from '@/types/permission.types';
import { ROLE_ENDPOINTS } from '@/api/endpoints/role.endpoints';
import { PERMISSION_ENDPOINTS } from '@/api/endpoints/permission.endpoints';
import { ManagementApiService, FormApiService } from '@/types/management.types';

export const RoleManagementApiService: ManagementApiService<Role, RoleFilterParams> = {
  getAll: async (offset = 0, limit = 10, filters?: RoleFilterParams): Promise<PaginatedRolesResponse> => {
    const query = buildQueryString({
      ...filters,
      offset,
      limit: Math.min(filters?.limit ?? limit, 100),
    });
    return (await axiosInstance.get<PaginatedRolesResponse>(`${ROLE_ENDPOINTS.ROLES}${query}`)).data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(ROLE_ENDPOINTS.ROLE_DETAIL(id));
  },
  exportItems: async (format: ExportFormat, filters?: Omit<RoleFilterParams, 'offset' | 'limit'>): Promise<Blob> => {
    const query = buildQueryString({ ...filters, format: format.toLowerCase() });
    return (await axiosInstance.get<Blob>(`${ROLE_ENDPOINTS.ROLES_EXPORT}${query}`, { responseType: 'blob' })).data;
  },
};

export const RoleFormApiService: FormApiService<Role, RoleCreate, RoleUpdate> = {
  getItem: async (id: number | string): Promise<Role> =>
    (await axiosInstance.get<Role>(ROLE_ENDPOINTS.ROLE_DETAIL(id))).data,
  createItem: async (data: RoleCreate): Promise<Role> =>
    (await axiosInstance.post<Role>(ROLE_ENDPOINTS.ROLES, data)).data,
  updateItem: async (id: number, data: RoleUpdate): Promise<Role> =>
    (await axiosInstance.put<Role>(ROLE_ENDPOINTS.ROLE_DETAIL(id), data)).data,
};

export const RoleApi = {
  Management: RoleManagementApiService,
  Form: RoleFormApiService,
  getRoles: RoleManagementApiService.getAll,
  getRoleById: async (id: number | string): Promise<Role> =>
    (await axiosInstance.get<Role>(ROLE_ENDPOINTS.ROLE_DETAIL(id))).data,
  createRole: RoleFormApiService.createItem,
  updateRole: RoleFormApiService.updateItem,
  deleteRole: RoleManagementApiService.deleteItem,
  exportRoles: RoleManagementApiService.exportItems,
  getPermissions: async (offset = 0, limit = 20, filters?: PermissionFilterParams): Promise<PaginatedPermissionsResponse> => {
    const query = buildQueryString({ ...filters, offset, limit });
    return (await axiosInstance.get<PaginatedPermissionsResponse>(`${PERMISSION_ENDPOINTS.PERMISSIONS}${query}`)).data;
  },
};